// src/pages/AgendaCalendarPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Container, Modal, Form, Button, Alert, Dropdown, Spinner, Row, Col, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { useAgendaAuth } from '../context/AgendaAuthContext.js';
import { useLanguage } from '../context/LanguageContext.js';
import { useGoogleMaps } from '../hooks/useGoogleMaps.js';
import './CalendarPage.css';

export default function AgendaCalendarPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading: authLoading, logout, updateProfile } = useAgendaAuth();
  const { language, setLanguage } = useLanguage();
  const calendarRef = useRef(null);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Translations
  const t = {
    pt: {
      title: 'Agenda Pessoal',
      myProfile: 'Meu Perfil',
      logout: 'Sair',
      menu: 'Menu',
      createEvent: '+ Criar Evento',
      newEvent: 'Novo Evento',
      editEvent: 'Editar Evento',
      eventTitle: 'Titulo',
      date: 'Data',
      startTime: 'Inicio',
      endTime: 'Fim',
      optional: 'opcional',
      location: 'Localizacao',
      typeAddress: 'Digite o endereco...',
      description: 'Descricao',
      repeat: 'Repetir',
      noRepeat: 'Nao repetir',
      daily: 'Diario',
      weekly: 'Semanal',
      biweekly: 'A cada 14 dias',
      monthly: 'Mensal',
      repeatUntil: 'Repetir ate',
      repeatUntilHint: 'Padrao: 1 ano a partir da data inicial',
      whatsappReminders: 'Lembretes WhatsApp',
      addReminder: '+ Adicionar',
      noReminders: 'Nenhum lembrete configurado',
      before: 'antes',
      remove: 'Remover',
      delete: 'Excluir',
      cancel: 'Cancelar',
      save: 'Salvar',
      saving: 'Salvando...',
      deleteEvent: 'Excluir Evento',
      deleteRecurring: 'Este e um evento recorrente. O que deseja fazer?',
      deleteConfirm: 'Tem certeza que deseja excluir este evento?',
      deleteOnlyThis: 'Excluir apenas este',
      deleteThisAndFuture: 'Excluir este e futuros',
      profile: 'Meu Perfil',
      email: 'Email',
      name: 'Nome',
      whatsappNumber: 'WhatsApp (para lembretes)',
      whatsappFormat: 'Formato: 64XXXXXXXXX (ex: 64275954995)',
      requiredFields: 'Titulo, data e hora sao obrigatorios',
      eventCreated: 'Evento criado',
      eventUpdated: 'Evento atualizado',
      eventDeleted: 'Evento excluido',
      eventMoved: 'Evento movido com sucesso',
      profileUpdated: 'Perfil atualizado',
      loadError: 'Falha ao carregar eventos',
      moveError: 'Falha ao mover evento',
      saveError: 'Falha ao salvar evento',
      deleteError: 'Falha ao excluir evento',
      invalidFormat: 'Formato invalido (use HH:MM)',
      duplicateReminder: 'Este lembrete ja existe',
      minutes: 'minutos',
      hour: 'hora',
      hours: 'horas',
      day: 'dia',
      days: 'dias'
    },
    en: {
      title: 'Personal Agenda',
      myProfile: 'My Profile',
      logout: 'Logout',
      menu: 'Menu',
      createEvent: '+ Create Event',
      newEvent: 'New Event',
      editEvent: 'Edit Event',
      eventTitle: 'Title',
      date: 'Date',
      startTime: 'Start',
      endTime: 'End',
      optional: 'optional',
      location: 'Location',
      typeAddress: 'Type the address...',
      description: 'Description',
      repeat: 'Repeat',
      noRepeat: "Don't repeat",
      daily: 'Daily',
      weekly: 'Weekly',
      biweekly: 'Every 14 days',
      monthly: 'Monthly',
      repeatUntil: 'Repeat until',
      repeatUntilHint: 'Default: 1 year from start date',
      whatsappReminders: 'WhatsApp Reminders',
      addReminder: '+ Add',
      noReminders: 'No reminders configured',
      before: 'before',
      remove: 'Remove',
      delete: 'Delete',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving...',
      deleteEvent: 'Delete Event',
      deleteRecurring: 'This is a recurring event. What would you like to do?',
      deleteConfirm: 'Are you sure you want to delete this event?',
      deleteOnlyThis: 'Delete only this',
      deleteThisAndFuture: 'Delete this and future',
      profile: 'My Profile',
      email: 'Email',
      name: 'Name',
      whatsappNumber: 'WhatsApp (for reminders)',
      whatsappFormat: 'Format: 64XXXXXXXXX (e.g., 64275954995)',
      requiredFields: 'Title, date and time are required',
      eventCreated: 'Event created',
      eventUpdated: 'Event updated',
      eventDeleted: 'Event deleted',
      eventMoved: 'Event moved successfully',
      profileUpdated: 'Profile updated',
      loadError: 'Failed to load events',
      moveError: 'Failed to move event',
      saveError: 'Failed to save event',
      deleteError: 'Failed to delete event',
      invalidFormat: 'Invalid format (use HH:MM)',
      duplicateReminder: 'This reminder already exists',
      minutes: 'minutes',
      hour: 'hour',
      hours: 'hours',
      day: 'day',
      days: 'days'
    }
  };
  const txt = t[language] || t.en;

  // Google Maps address autocomplete
  const { addressService, aucklandBounds, isLoaded } = useGoogleMaps();
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const addressDebounceRef = useRef(null);
  const addressSessionTokenRef = useRef(null);

  // Event modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventDate: '',
    eventTime: '09:00',
    eventEndTime: '',
    address: '',
    reminderConfig: [120],
    isRecurring: false,
    recurrenceRule: 'weekly',
    recurrenceEndDate: ''
  });

  // Reminder input state (HH:MM format)
  const [reminderInput, setReminderInput] = useState('01:00');

  // Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    whatsappNumber: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const API_URL = process.env.REACT_APP_API_URL;

  // Detect mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/agenda/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Load profile data
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        whatsappNumber: user.whatsappNumber || ''
      });
    }
  }, [user]);

  // Time options for dropdown (06:00 to 22:00 in 15-minute intervals)
  const timeOptions = useMemo(() => {
    const options = [];
    for (let hour = 6; hour <= 22; hour++) {
      for (let min = 0; min < 60; min += 15) {
        if (hour === 22 && min > 0) break;
        const h = String(hour).padStart(2, '0');
        const m = String(min).padStart(2, '0');
        options.push(`${h}:${m}`);
      }
    }
    return options;
  }, []);

  // Helper functions for HH:MM <-> minutes conversion
  const hhmmToMinutes = useCallback((hhmm) => {
    const parts = hhmm.split(':');
    if (parts.length !== 2) return null;
    const hh = parseInt(parts[0], 10);
    const mm = parseInt(parts[1], 10);
    if (isNaN(hh) || isNaN(mm) || mm < 0 || mm > 59) return null;
    return hh * 60 + mm;
  }, []);

  const minutesToHHMM = useCallback((mins) => {
    const hh = Math.floor(mins / 60);
    const mm = mins % 60;
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }, []);

  const minutesToFriendlyText = useCallback((mins) => {
    if (mins < 60) {
      return `${mins} ${txt.minutes}`;
    } else if (mins === 60) {
      return `1 ${txt.hour}`;
    } else if (mins < 1440) {
      const hours = Math.floor(mins / 60);
      const minutes = mins % 60;
      if (minutes === 0) {
        return `${hours} ${txt.hours}`;
      }
      return `${hours}h${minutes}min`;
    } else if (mins === 1440) {
      return `1 ${txt.day}`;
    } else {
      const days = Math.floor(mins / 1440);
      return `${days} ${txt.days}`;
    }
  }, [txt]);

  // Add reminder to list
  const handleAddReminder = useCallback(() => {
    const minutes = hhmmToMinutes(reminderInput);
    if (minutes === null || minutes < 1) {
      alert(txt.invalidFormat);
      return;
    }
    if (formData.reminderConfig.includes(minutes)) {
      alert(txt.duplicateReminder);
      return;
    }
    setFormData(prev => ({
      ...prev,
      reminderConfig: [...prev.reminderConfig, minutes].sort((a, b) => b - a)
    }));
    setReminderInput('01:00');
  }, [reminderInput, formData.reminderConfig, hhmmToMinutes, txt]);

  // Remove reminder from list
  const handleRemoveReminder = useCallback((minutes) => {
    setFormData(prev => ({
      ...prev,
      reminderConfig: prev.reminderConfig.filter(m => m !== minutes)
    }));
  }, []);

  // Handle address change with Google Maps autocomplete (com debounce 300ms + session token)
  const handleAddressChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, address: value }));

    if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);

    if (value.length < 3 || !addressService) {
      setAddressSuggestions([]);
      return;
    }

    if (!addressSessionTokenRef.current && window.google?.maps?.places) {
      addressSessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }

    addressDebounceRef.current = setTimeout(() => {
      addressService.getPlacePredictions(
        {
          input: value,
          bounds: aucklandBounds,
          componentRestrictions: { country: 'nz' },
          sessionToken: addressSessionTokenRef.current
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setAddressSuggestions(predictions);
          } else {
            setAddressSuggestions([]);
          }
        }
      );
    }, 300);
  }, [addressService, aucklandBounds]);

  // Load events
  const loadEvents = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/agenda/events`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setEvents(data.events || []);
      } else {
        setError(txt.loadError);
      }
    } catch (err) {
      setError(txt.loadError);
    } finally {
      setLoading(false);
    }
  }, [token, API_URL, txt.loadError]);

  useEffect(() => {
    if (isAuthenticated) {
      loadEvents();
    }
  }, [isAuthenticated, loadEvents]);

  // Convert to calendar events
  const calendarEvents = useMemo(() => {
    return events.map(event => ({
      id: event.id,
      title: event.title,
      start: `${event.eventDate}T${event.eventTime}`,
      end: event.eventEndTime ? `${event.eventDate}T${event.eventEndTime}` : undefined,
      backgroundColor: '#007bff',
      borderColor: '#007bff',
      extendedProps: {
        description: event.description,
        address: event.address,
        reminderConfig: event.reminderMinutes,
        remindersSent: event.remindersSent,
        isRecurring: event.isRecurring,
        recurrenceRule: event.recurrenceRule,
        recurrenceGroupId: event.recurrenceGroupId
      }
    }));
  }, [events]);

  // Handle date select (create new event)
  const handleDateClick = useCallback((info) => {
    const startDate = info.start || new Date();
    const clickedDate = startDate.toISOString().split('T')[0];
    const clickedTime = startDate.toTimeString().substring(0, 5);

    setFormData({
      title: '',
      description: '',
      eventDate: clickedDate,
      eventTime: clickedTime,
      eventEndTime: '',
      address: '',
      reminderConfig: [120],
      isRecurring: false,
      recurrenceRule: 'weekly',
      recurrenceEndDate: ''
    });
    setSelectedEvent(null);
    setShowModal(true);
  }, []);

  // Handle event click (edit event)
  const handleEventClick = useCallback((info) => {
    const event = info.event;
    const props = event.extendedProps;

    let reminderConfig = [120];
    try {
      if (props.reminderConfig) {
        reminderConfig = typeof props.reminderConfig === 'string'
          ? JSON.parse(props.reminderConfig)
          : props.reminderConfig;
      }
    } catch (e) {
      reminderConfig = [120];
    }

    const startDate = event.start.toISOString().split('T')[0];
    const startTime = event.start.toTimeString().substring(0, 5);
    const endTime = event.end ? event.end.toTimeString().substring(0, 5) : '';

    setFormData({
      title: event.title,
      description: props.description || '',
      eventDate: startDate,
      eventTime: startTime,
      eventEndTime: endTime,
      address: props.address || '',
      reminderConfig,
      isRecurring: props.isRecurring || false,
      recurrenceRule: props.recurrenceRule || 'weekly',
      recurrenceEndDate: ''
    });

    setSelectedEvent({
      id: event.id,
      ...props
    });
    setShowModal(true);
  }, []);

  // Handle event drop (drag and drop)
  const handleEventDrop = useCallback(async (info) => {
    const event = info.event;
    const newDate = event.start.toISOString().split('T')[0];
    const newTime = event.start.toTimeString().substring(0, 5);
    const newEndTime = event.end ? event.end.toTimeString().substring(0, 5) : null;

    try {
      const response = await fetch(`${API_URL}/api/agenda/events/${event.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          eventDate: newDate,
          eventTime: newTime,
          eventEndTime: newEndTime,
          resetReminders: true
        })
      });

      if (!response.ok) {
        throw new Error(txt.moveError);
      }

      setSuccess(txt.eventMoved);
      loadEvents();
    } catch (err) {
      setError(err.message);
      info.revert();
    }
  }, [token, API_URL, loadEvents, txt]);

  // Save event
  const handleSaveEvent = useCallback(async () => {
    if (!formData.title || !formData.eventDate || !formData.eventTime) {
      setError(txt.requiredFields);
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        eventDate: formData.eventDate,
        eventTime: formData.eventTime,
        eventEndTime: formData.eventEndTime || null,
        address: formData.address || null,
        reminderMinutes: formData.reminderConfig,
        isRecurring: formData.isRecurring,
        recurrenceRule: formData.recurrenceRule,
        recurrenceEndDate: formData.recurrenceEndDate || null
      };

      const url = selectedEvent
        ? `${API_URL}/api/agenda/events/${selectedEvent.id}`
        : `${API_URL}/api/agenda/events`;

      const response = await fetch(url, {
        method: selectedEvent ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || txt.saveError);
      }

      setSuccess(selectedEvent ? txt.eventUpdated : txt.eventCreated);
      setShowModal(false);
      loadEvents();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  }, [formData, selectedEvent, token, API_URL, loadEvents, txt]);

  // Delete event
  const handleDeleteConfirm = useCallback(async (mode) => {
    if (!selectedEvent) return;

    const scrollPos = window.scrollY;
    setActionLoading(true);
    setShowDeleteModal(false);
    setShowModal(false);

    try {
      const url = `${API_URL}/api/agenda/events/${selectedEvent.id}?mode=${mode}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(txt.deleteError);
      }

      setSuccess(txt.eventDeleted);
      loadEvents();
      requestAnimationFrame(() => window.scrollTo(0, scrollPos));
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  }, [selectedEvent, token, API_URL, loadEvents, txt]);

  // Update profile
  const handleUpdateProfile = useCallback(async () => {
    setProfileLoading(true);
    setError('');

    const result = await updateProfile(profileData.name, profileData.whatsappNumber);

    setProfileLoading(false);

    if (result.success) {
      setSuccess(txt.profileUpdated);
      setShowProfileModal(false);
    } else {
      setError(result.error);
    }
  }, [profileData, updateProfile, txt]);

  // Handle logout
  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/agenda/login');
  }, [logout, navigate]);

  // Create new event button handler
  const handleCreateNewEvent = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    setFormData({
      title: '',
      description: '',
      eventDate: today,
      eventTime: '09:00',
      eventEndTime: '',
      address: '',
      reminderConfig: [120],
      isRecurring: false,
      recurrenceRule: 'weekly',
      recurrenceEndDate: ''
    });
    setSelectedEvent(null);
    setShowModal(true);
  }, []);

  // Calendar options
  const calendarOptions = useMemo(() => ({
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
    initialView: isMobile ? 'timeGridDay' : 'timeGridWeek',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: isMobile ? 'timeGridDay,listWeek' : 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
    },
    firstDay: 1,
    slotMinTime: '06:00:00',
    slotMaxTime: '22:00:00',
    snapDuration: '00:15:00',
    allDaySlot: false,
    selectable: true,
    selectMirror: true,
    editable: true,
    height: 'auto',
    selectLongPressDelay: isMobile ? 500 : 0,  // Long press to create event on mobile
    longPressDelay: 500,                        // Long press to drag
    eventLongPressDelay: 500,                   // Long press to drag event
    dayHeaderContent: (arg) => {
      const day = arg.date.getDate().toString().padStart(2, '0');
      const month = (arg.date.getMonth() + 1).toString().padStart(2, '0');
      const locale = language === 'pt' ? 'pt-BR' : 'en-NZ';
      const weekday = arg.date.toLocaleDateString(locale, { weekday: 'short' }).replace('.', '');
      return `${weekday} ${day}/${month}`;
    },
    eventTimeFormat: {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    },
    slotLabelFormat: {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }
  }), [isMobile, language]);

  // Show loading while checking auth
  if (authLoading || loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: isMobile ? '12px 16px' : '16px 24px',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)'
      }}>
        <div>
          <h5 style={{
            margin: 0,
            fontWeight: '700',
            fontSize: isMobile ? '1.1rem' : '1.3rem',
            letterSpacing: '0.5px'
          }}>
            📅 {txt.title}
          </h5>
          <small style={{ opacity: 0.9, fontSize: isMobile ? '0.75rem' : '0.85rem' }}>{user?.email}</small>
        </div>
        <div className="d-flex align-items-center gap-2">
          {/* Language Selector */}
          <div className="d-flex gap-1 align-items-center" style={{
            background: 'rgba(255,255,255,0.15)',
            padding: '4px 8px',
            borderRadius: '20px'
          }}>
            <button
              type="button"
              onClick={() => setLanguage('pt')}
              style={{
                background: language === 'pt' ? 'rgba(255,255,255,0.9)' : 'transparent',
                border: 'none',
                borderRadius: '50%',
                padding: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                transform: language === 'pt' ? 'scale(1.1)' : 'scale(1)',
                boxShadow: language === 'pt' ? '0 2px 8px rgba(0,0,0,0.2)' : 'none'
              }}
              title="Português"
            >
              <img src="https://flagcdn.com/w40/br.png" alt="BR" width="24" height="16" style={{ borderRadius: '3px', display: 'block' }} />
            </button>
            <button
              type="button"
              onClick={() => setLanguage('en')}
              style={{
                background: language === 'en' ? 'rgba(255,255,255,0.9)' : 'transparent',
                border: 'none',
                borderRadius: '50%',
                padding: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                transform: language === 'en' ? 'scale(1.1)' : 'scale(1)',
                boxShadow: language === 'en' ? '0 2px 8px rgba(0,0,0,0.2)' : 'none'
              }}
              title="English"
            >
              <img src="https://flagcdn.com/w40/nz.png" alt="NZ" width="24" height="16" style={{ borderRadius: '3px', display: 'block' }} />
            </button>
          </div>
          <Dropdown>
            <Dropdown.Toggle
              variant="light"
              size="sm"
              style={{
                background: 'rgba(255,255,255,0.95)',
                border: 'none',
                fontWeight: '500',
                borderRadius: '20px',
                padding: '6px 14px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}
            >
              {user?.name?.split(' ')[0] || txt.menu}
            </Dropdown.Toggle>
            <Dropdown.Menu align="end" style={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
              <Dropdown.Item onClick={() => setShowProfileModal(true)} style={{ padding: '10px 16px' }}>
                👤 {txt.myProfile}
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleLogout} style={{ color: '#dc3545', padding: '10px 16px' }}>
                🚪 {txt.logout}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <Container fluid className="py-3">
        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Create button (mobile) */}
        {isMobile && (
          <Button
            className="w-100 mb-3"
            onClick={handleCreateNewEvent}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '12px',
              padding: '12px',
              fontWeight: '600',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.35)'
            }}
          >
            {txt.createEvent}
          </Button>
        )}

        {/* Instruction text for mobile */}
        {isMobile && (
          <p className="text-muted text-center mb-2" style={{ fontSize: '0.8rem' }}>
            {language === 'pt' ? 'Segure para criar evento' : 'Long press to create event'}
          </p>
        )}

        {/* Calendar with light theme */}
        <div className="calendar-container light-theme">
          <FullCalendar
            ref={calendarRef}
            {...calendarOptions}
            events={calendarEvents}
            eventClick={handleEventClick}
            select={handleDateClick}
            eventDrop={handleEventDrop}
          />
        </div>
      </Container>

      {/* Event Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderBottom: 'none'
        }}>
          <Modal.Title style={{ fontWeight: '600' }}>
            {selectedEvent ? `✏️ ${txt.editEvent}` : `✨ ${txt.newEvent}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{txt.eventTitle} <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                autoFocus
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{txt.date} <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                value={formData.eventDate}
                onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
              />
            </Form.Group>

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>{txt.startTime} <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formData.eventTime}
                    onChange={(e) => setFormData({ ...formData, eventTime: e.target.value })}
                  >
                    {timeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>{txt.endTime} <span className="text-muted">({txt.optional})</span></Form.Label>
                  <Form.Select
                    value={formData.eventEndTime}
                    onChange={(e) => setFormData({ ...formData, eventEndTime: e.target.value })}
                  >
                    <option value="">--</option>
                    {timeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Address with Google Maps autocomplete */}
            <Form.Group className="mb-3 position-relative">
              <Form.Label>{txt.location} <span className="text-muted">({txt.optional})</span></Form.Label>
              <Form.Control
                type="text"
                value={formData.address}
                onChange={(e) => handleAddressChange(e.target.value)}
                placeholder={txt.typeAddress}
              />
              {addressSuggestions.length > 0 && (
                <ul
                  className="list-group position-absolute w-100 mt-1 shadow-sm"
                  style={{ zIndex: 1051, maxHeight: '200px', overflowY: 'auto' }}
                >
                  {addressSuggestions.map((pred) => (
                    <li
                      key={pred.place_id}
                      className="list-group-item list-group-item-action"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, address: pred.description }));
                        setAddressSuggestions([]);
                        addressSessionTokenRef.current = null;
                      }}
                    >
                      {pred.description}
                    </li>
                  ))}
                </ul>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{txt.description} <span className="text-muted">({txt.optional})</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Form.Group>

            {/* Recurrence Configuration */}
            <Form.Group className="mb-3">
              <Form.Label>{txt.repeat}</Form.Label>
              <Form.Select
                value={formData.isRecurring ? formData.recurrenceRule : ''}
                onChange={(e) => {
                  if (e.target.value === '') {
                    setFormData({ ...formData, isRecurring: false, recurrenceRule: 'weekly' });
                  } else {
                    setFormData({ ...formData, isRecurring: true, recurrenceRule: e.target.value });
                  }
                }}
              >
                <option value="">{txt.noRepeat}</option>
                <option value="daily">{txt.daily}</option>
                <option value="weekly">{txt.weekly}</option>
                <option value="biweekly">{txt.biweekly}</option>
                <option value="monthly">{txt.monthly}</option>
              </Form.Select>
            </Form.Group>

            {formData.isRecurring && (
              <Form.Group className="mb-3">
                <Form.Label>{txt.repeatUntil} <span className="text-muted">({txt.optional})</span></Form.Label>
                <Form.Control
                  type="date"
                  value={formData.recurrenceEndDate}
                  onChange={(e) => setFormData({ ...formData, recurrenceEndDate: e.target.value })}
                  min={formData.eventDate}
                />
                <Form.Text className="text-muted">
                  {txt.repeatUntilHint}
                </Form.Text>
              </Form.Group>
            )}

            {/* WhatsApp Reminders Configuration */}
            <Form.Group className="mb-3">
              <Form.Label>{txt.whatsappReminders}</Form.Label>

              {/* Add new reminder */}
              <div className="d-flex gap-2 mb-2">
                <Form.Control
                  type="text"
                  value={reminderInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\d:]/g, '');
                    if (val.length <= 5) setReminderInput(val);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddReminder();
                    }
                  }}
                  placeholder="HH:MM"
                  style={{ maxWidth: '100px' }}
                />
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleAddReminder}
                >
                  {txt.addReminder}
                </Button>
              </div>

              {/* List of configured reminders */}
              <div className="d-flex flex-wrap gap-2">
                {formData.reminderConfig.length === 0 ? (
                  <span className="text-muted small">{txt.noReminders}</span>
                ) : (
                  formData.reminderConfig.map((minutes) => (
                    <Badge
                      key={minutes}
                      bg="info"
                      className="d-flex align-items-center gap-1 py-2 px-2"
                      style={{ fontSize: '0.85rem' }}
                    >
                      {minutesToHHMM(minutes)} ({minutesToFriendlyText(minutes)} {txt.before})
                      <span
                        style={{ cursor: 'pointer', marginLeft: '4px' }}
                        onClick={() => handleRemoveReminder(minutes)}
                        title={txt.remove}
                      >
                        x
                      </span>
                    </Badge>
                  ))
                )}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div>
            {selectedEvent && (
              <Button
                variant="outline-danger"
                onClick={() => setShowDeleteModal(true)}
                disabled={actionLoading}
              >
                {txt.delete}
              </Button>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              {txt.cancel}
            </Button>
            <Button variant="primary" onClick={handleSaveEvent} disabled={actionLoading}>
              {actionLoading ? txt.saving : txt.save}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{txt.deleteEvent}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEvent?.isRecurring ? (
            <p>{txt.deleteRecurring}</p>
          ) : (
            <p>{txt.deleteConfirm}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            {txt.cancel}
          </Button>
          <Button variant="danger" onClick={() => handleDeleteConfirm('single')}>
            {selectedEvent?.isRecurring ? txt.deleteOnlyThis : txt.delete}
          </Button>
          {selectedEvent?.isRecurring && (
            <Button variant="danger" onClick={() => handleDeleteConfirm('future')}>
              {txt.deleteThisAndFuture}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Profile Modal */}
      <Modal show={showProfileModal} onHide={() => setShowProfileModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{txt.profile}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{txt.email}</Form.Label>
              <Form.Control
                type="email"
                value={user?.email || ''}
                readOnly
                style={{ backgroundColor: '#f0f0f0' }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{txt.name}</Form.Label>
              <Form.Control
                type="text"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{txt.whatsappNumber}</Form.Label>
              <Form.Control
                type="tel"
                value={profileData.whatsappNumber}
                onChange={(e) => setProfileData({ ...profileData, whatsappNumber: e.target.value })}
              />
              <Form.Text className="text-muted">
                {txt.whatsappFormat}
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProfileModal(false)}>
            {txt.cancel}
          </Button>
          <Button variant="primary" onClick={handleUpdateProfile} disabled={profileLoading}>
            {profileLoading ? txt.saving : txt.save}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
